import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Role {
  id: string;
  name: string;
  color: number;
  position: number;
}

interface Props {
  guildId: string;
  value: string | null;
  onChange: (roleId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Hide @everyone (usually not selectable in configs). Default: true. */
  hideEveryone?: boolean;
}

/**
 * Native <select> populated from the guild's roles (fetched via
 * GET /api/guilds/:id/roles). Sorted by position descending (top-to-bottom
 * matches Discord's UI).
 */
export function RolePicker({
  guildId,
  value,
  onChange,
  placeholder = '— Rolle wählen —',
  disabled,
  className,
  hideEveryone = true,
}: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guild-roles', guildId],
    queryFn: () => api<{ roles: Role[] }>(`/api/guilds/${guildId}/roles`),
    enabled: !!guildId,
    staleTime: 30_000,
  });

  const roles = (data?.roles ?? [])
    .filter((r) => !hideEveryone || r.id !== guildId /* @everyone == guildId */)
    .sort((a, b) => b.position - a.position);

  return (
    <div>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || isLoading || !!error}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          className
        )}
      >
        <option value="">
          {isLoading ? 'Lade Rollen…' : error ? 'Fehler beim Laden' : placeholder}
        </option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-xs text-destructive">{(error as Error).message}</p>
      )}
    </div>
  );
}
